import { useState, useEffect } from 'react';
import { useLang } from '../../../hooks/useLang';
import PlatformRatingModal from './PlatformRatingModal';

type LangKey = 'zh' | 'en' | 'fr' | 'es' | 'pt' | 'ja' | 'de' | 'ko' | 'ar' | 'ru';

interface PlatformItem {
  name: string;
  nameEn: string;
  desc: string;
  descEn: string;
  url: string;
  icon: string;
  tags: Record<LangKey, string[]>;
  highlight?: string;
}

interface PlatformCategory {
  id: string;
  label: string;
  labelEn: string;
  icon: string;
  color: string;
  items: PlatformItem[];
}

// Helper to build tags for all languages from zh/en pair
function mkTags(zh: string[], en: string[], fr: string[], ja: string[], de: string[], ko: string[], ar: string[], ru: string[]): Record<LangKey, string[]> {
  return { zh, en, fr, es: en, pt: en, ja, de, ko, ar, ru };
}

const PLATFORM_DATA: PlatformCategory[] = [
  {
    id: 'data',
    label: '数据与研究平台',
    labelEn: 'Data & Research Platforms',
    icon: 'ri-database-2-line',
    color: '#7C3AED',
    items: [
      {
        name: 'RWA.xyz', nameEn: 'RWA.xyz',
        desc: '全球RWA数据聚合平台，实时显示TVL总锁仓价值、项目数量、赛道分布、交易量，是RWA行业数据的权威来源。',
        descEn: 'The global RWA data aggregator — real-time TVL, project count, sector breakdown, and volume. The definitive data source for the RWA industry.',
        url: 'https://rwa.xyz', icon: 'ri-bar-chart-grouped-line',
        tags: mkTags(
          ['TVL数据', '实时监控', '项目追踪'],
          ['TVL Data', 'Real-time', 'Project Tracking'],
          ['Données TVL', 'Temps réel', 'Suivi de projets'],
          ['TVLデータ', 'リアルタイム', 'プロジェクト追跡'],
          ['TVL-Daten', 'Echtzeit', 'Projektverfolgung'],
          ['TVL 데이터', '실시간', '프로젝트 추적'],
          ['بيانات TVL', 'الوقت الفعلي', 'تتبع المشاريع'],
          ['Данные TVL', 'Реальное время', 'Отслеживание проектов'],
        ),
        highlight: '行业数据首选',
      },
      {
        name: 'DeFiLlama', nameEn: 'DeFiLlama',
        desc: '覆盖全球DeFi生态的数据聚合器，提供RWA赛道TVL、协议对比、跨链数据分析等功能，免费且开源。',
        descEn: 'DeFi data aggregator covering the full ecosystem — RWA sector TVL, protocol comparison, and cross-chain analytics. Free and open-source.',
        url: 'https://defillama.com', icon: 'ri-line-chart-line',
        tags: mkTags(
          ['DeFi数据', 'TVL排名', '免费开源'],
          ['DeFi Data', 'TVL Ranking', 'Open Source'],
          ['Données DeFi', 'Classement TVL', 'Open Source'],
          ['DeFiデータ', 'TVLランキング', 'オープンソース'],
          ['DeFi-Daten', 'TVL-Ranking', 'Open Source'],
          ['DeFi 데이터', 'TVL 순위', '오픈 소스'],
          ['بيانات DeFi', 'تصنيف TVL', 'مفتوح المصدر'],
          ['Данные DeFi', 'Рейтинг TVL', 'Открытый исходный код'],
        ),
      },
      {
        name: 'Chainlink官网', nameEn: 'Chainlink Official',
        desc: '预言机技术文档、RWA集成案例、开发者工具下载的权威来源，提供RWA数据喂送方案与API接口文档。',
        descEn: 'Official source for oracle technical docs, RWA integration case studies, and developer tools. Essential for RWA data feed integration.',
        url: 'https://chain.link', icon: 'ri-link',
        tags: mkTags(
          ['技术文档', 'RWA集成', '开发者工具'],
          ['Tech Docs', 'RWA Integration', 'Dev Tools'],
          ['Docs Techniques', 'Intégration RWA', 'Outils Dev'],
          ['技術ドキュメント', 'RWA統合', '開発者ツール'],
          ['Technische Docs', 'RWA-Integration', 'Entwicklertools'],
          ['기술 문서', 'RWA 통합', '개발자 도구'],
          ['وثائق تقنية', 'تكامل RWA', 'أدوات المطورين'],
          ['Технические документы', 'Интеграция RWA', 'Инструменты разработчика'],
        ),
      },
      {
        name: 'Galaxy Digital Research', nameEn: 'Galaxy Digital Research',
        desc: '发布机构级RWA研究报告，涵盖市场趋势、定价模型、监管动态，视角专业，数据支撑充分。',
        descEn: 'Institutional-grade RWA research reports covering market trends, pricing models, and regulatory updates. Professional and data-backed.',
        url: 'https://www.galaxy.com/research', icon: 'ri-file-chart-line',
        tags: mkTags(
          ['机构研究', 'RWA报告', '市场分析'],
          ['Institutional Research', 'RWA Reports', 'Market Analysis'],
          ['Recherche Institutionnelle', 'Rapports RWA', 'Analyse de Marché'],
          ['機関研究', 'RWAレポート', '市場分析'],
          ['Institutionelle Forschung', 'RWA-Berichte', 'Marktanalyse'],
          ['기관 연구', 'RWA 보고서', '시장 분석'],
          ['بحث مؤسسي', 'تقارير RWA', 'تحليل السوق'],
          ['Институциональные исследования', 'Отчёты RWA', 'Анализ рынка'],
        ),
        highlight: '机构首选',
      },
      {
        name: 'BCG数字资产报告', nameEn: 'BCG Digital Assets',
        desc: '波士顿咨询集团发布的数字资产与RWA市场规模预测报告，为$16万亿RWA市场预测的来源，是行业战略规划的权威参考。',
        descEn: "BCG's authoritative digital asset and RWA market forecasts — the source of the famous $16 trillion tokenization opportunity estimate.",
        url: 'https://www.bcg.com/capabilities/digital-technology-data/fintech', icon: 'ri-pie-chart-line',
        tags: mkTags(
          ['市场规模', '战略研究', '权威预测'],
          ['Market Size', 'Strategic Research', 'Authoritative Forecast'],
          ['Taille du Marché', 'Recherche Stratégique', 'Prévision Autoritaire'],
          ['市場規模', '戦略研究', '権威ある予測'],
          ['Marktgröße', 'Strategische Forschung', 'Maßgebliche Prognose'],
          ['시장 규모', '전략 연구', '권위 있는 예측'],
          ['حجم السوق', 'بحث استراتيجي', 'توقعات موثوقة'],
          ['Размер рынка', 'Стратегические исследования', 'Авторитетный прогноз'],
        ),
      },
    ],
  },
  {
    id: 'compliance',
    label: '合规RWA发行平台',
    labelEn: 'Compliant RWA Issuance Platforms',
    icon: 'ri-shield-check-line',
    color: '#10B981',
    items: [
      {
        name: 'Centrifuge', nameEn: 'Centrifuge',
        desc: '全球最早成熟运营的DeFi+RWA代币化平台，专注企业应收账款、贸易融资、SME信贷的链上代币化。采用"优先/劣后"分层结构（DROP/TIN），与MakerDAO、Aave等头部DeFi协议深度整合，TVL超6亿美元。',
        descEn: 'One of the earliest mature DeFi+RWA platforms, focused on tokenizing accounts receivable, trade finance, and SME credit. Uses Senior/Junior (DROP/TIN) structure, deeply integrated with MakerDAO and Aave. TVL exceeds $600M.',
        url: 'https://centrifuge.io', icon: 'ri-anchor-line',
        tags: mkTags(
          ['应收账款RWA', 'DeFi整合', 'TVL $6亿+'],
          ['Receivables RWA', 'DeFi Integration', 'TVL $600M+'],
          ['RWA Créances', 'Intégration DeFi', 'TVL 600M$+'],
          ['売掛金RWA', 'DeFi統合', 'TVL $6億+'],
          ['Forderungen RWA', 'DeFi-Integration', 'TVL $600M+'],
          ['매출채권 RWA', 'DeFi 통합', 'TVL $6억+'],
          ['RWA المستحقات', 'تكامل DeFi', 'TVL $600M+'],
          ['RWA дебиторской задолженности', 'Интеграция DeFi', 'TVL $600M+'],
        ),
        highlight: 'DeFi+RWA标杆',
      },
      {
        name: 'RealT', nameEn: 'RealT',
        desc: '全球最大的代币化房地产平台，将美国底特律、芝加哥等城市的单/多户住宅代币化为REITs份额，投资者从50美元起购，每周自动通过智能合约分配租金，已代币化200+栋物业，总价值超5000万美元。',
        descEn: "The world's largest tokenized real estate platform. Tokenizes US residential properties into REITs shares starting from $50. Weekly automatic rent distribution via smart contracts. 200+ properties tokenized worth $50M+.",
        url: 'https://realt.co', icon: 'ri-home-4-line',
        tags: mkTags(
          ['房产代币化', '周分红', '碎片化投资'],
          ['Real Estate RWA', 'Weekly Dividends', 'Fractional Investment'],
          ['Immobilier RWA', 'Dividendes Hebdo', 'Investissement Fractionné'],
          ['不動産RWA', '週次配当', '分割投資'],
          ['Immobilien RWA', 'Wöchentliche Dividenden', 'Bruchteilsinvestition'],
          ['부동산 RWA', '주간 배당', '분할 투자'],
          ['RWA العقارات', 'أرباح أسبوعية', 'استثمار جزئي'],
          ['RWA недвижимости', 'Еженедельные дивиденды', 'Дробное инвестирование'],
        ),
        highlight: '最大代币化房产',
      },
      {
        name: 'Maple Finance', nameEn: 'Maple Finance',
        desc: '机构级链上信贷市场，为VC、对冲基金、加密机构提供无抵押/低抵押贷款。贷款以RWA代币形式发行，流动性提供者获取利差收益。2023年推出现实世界信贷池（Real-World Lending），TVL超5亿美元。',
        descEn: 'Institutional on-chain credit market providing uncollateralized/under-collateralized loans to VCs, hedge funds, and crypto institutions. Launched Real-World Lending in 2023. TVL $500M+.',
        url: 'https://maple.finance', icon: 'ri-bank-line',
        tags: mkTags(
          ['机构信贷', '无抵押贷款', 'TVL $5亿+'],
          ['Institutional Credit', 'Uncollateralized Loans', 'TVL $500M+'],
          ['Crédit Institutionnel', 'Prêts Non Garantis', 'TVL 500M$+'],
          ['機関信用', '無担保ローン', 'TVL $5億+'],
          ['Institutioneller Kredit', 'Unbesicherte Kredite', 'TVL $500M+'],
          ['기관 신용', '무담보 대출', 'TVL $5억+'],
          ['ائتمان مؤسسي', 'قروض غير مضمونة', 'TVL $500M+'],
          ['Институциональный кредит', 'Необеспеченные займы', 'TVL $500M+'],
        ),
      },
      {
        name: 'Goldfinch', nameEn: 'Goldfinch',
        desc: '专注新兴市场（非洲、拉丁美洲、东南亚）中小企业融资的RWA信贷协议。由信用评估员（Auditors）和流动性提供者（LPs）共同参与，为发展中国家SME提供链上融资，将普惠金融与DeFi深度结合。',
        descEn: 'RWA credit protocol focused on emerging market (Africa, Latin America, Southeast Asia) SME financing. Combines credit assessors and liquidity providers to fund developing country SMEs, merging inclusive finance with DeFi.',
        url: 'https://goldfinch.finance', icon: 'ri-global-line',
        tags: mkTags(
          ['新兴市场', '普惠金融', 'SME融资'],
          ['Emerging Markets', 'Inclusive Finance', 'SME Financing'],
          ['Marchés Émergents', 'Finance Inclusive', 'Financement PME'],
          ['新興市場', '包括的金融', 'SME融資'],
          ['Schwellenmärkte', 'Inklusive Finanzen', 'KMU-Finanzierung'],
          ['신흥 시장', '포용적 금융', 'SME 금융'],
          ['الأسواق الناشئة', 'التمويل الشامل', 'تمويل الشركات الصغيرة'],
          ['Развивающиеся рынки', 'Инклюзивные финансы', 'Финансирование МСП'],
        ),
        highlight: '发展中国家首选',
      },
      {
        name: 'Tokeny Solutions', nameEn: 'Tokeny Solutions',
        desc: '欧洲领先的RWA代币化基础设施提供商，基于ERC-3643（T-REX协议）构建。提供KYC身份注册表（OnchainID）、合规代币合约、白名单管理、投资者档案管理全套解决方案，已为100+机构发行合规RWA代币。',
        descEn: "Europe's leading RWA tokenization infrastructure provider based on ERC-3643 (T-REX Protocol). Provides KYC identity registry (OnchainID), compliant token contracts, and whitelist management. 100+ institutional issuers.",
        url: 'https://tokeny.com', icon: 'ri-shield-check-line',
        tags: mkTags(
          ['ERC-3643', 'T-REX协议', 'OnchainID'],
          ['ERC-3643', 'T-REX Protocol', 'OnchainID'],
          ['ERC-3643', 'Protocole T-REX', 'OnchainID'],
          ['ERC-3643', 'T-REXプロトコル', 'OnchainID'],
          ['ERC-3643', 'T-REX-Protokoll', 'OnchainID'],
          ['ERC-3643', 'T-REX 프로토콜', 'OnchainID'],
          ['ERC-3643', 'بروتوكول T-REX', 'OnchainID'],
          ['ERC-3643', 'Протокол T-REX', 'OnchainID'],
        ),
      },
      {
        name: 'Securitize', nameEn: 'Securitize',
        desc: '全球最大RWA代币化技术服务商，持有美国SEC注册经纪交易商资质+香港SFC 1/4/9类牌照，为BlackRock BUIDL（$10亿+）、KKR私募、Hamilton Lane信贷提供代币化发行服务，是RWA机构发行的事实标准平台。',
        descEn: "World's largest RWA tokenization technology provider. SEC-registered broker-dealer + HK SFC 1/4/9-class licensed. Powers BlackRock BUIDL ($1B+), KKR, and Hamilton Lane tokenization.",
        url: 'https://securitize.io', icon: 'ri-secure-payment-line',
        tags: mkTags(
          ['美国+香港双牌照', 'BlackRock合作', '机构RWA'],
          ['US+HK Dual License', 'BlackRock Partner', 'Institutional RWA'],
          ['Double Licence US+HK', 'Partenaire BlackRock', 'RWA Institutionnel'],
          ['米国+香港デュアルライセンス', 'BlackRockパートナー', '機関RWA'],
          ['US+HK Doppellizenz', 'BlackRock-Partner', 'Institutionelles RWA'],
          ['미국+홍콩 이중 라이선스', 'BlackRock 파트너', '기관 RWA'],
          ['ترخيص مزدوج US+HK', 'شريك BlackRock', 'RWA المؤسسي'],
          ['Двойная лицензия US+HK', 'Партнёр BlackRock', 'Институциональный RWA'],
        ),
        highlight: '全球最大RWA平台',
      },
      {
        name: 'Tokeny (T-REX)', nameEn: 'Tokeny (T-REX Protocol)',
        desc: '欧洲领先的RWA代币化平台，基于ERC-3643（T-REX协议）合规代币标准，内置OnchainID链上身份注册系统，已为BNP Paribas资产管理等100+机构发行合规代币，持有欧盟MiCA合规认证。',
        descEn: "Europe's leading RWA platform based on ERC-3643 (T-REX Protocol) with OnchainID. 100+ institutional issuers including BNP Paribas Asset Management. MiCA compliant.",
        url: 'https://tokeny.com', icon: 'ri-building-2-line',
        tags: mkTags(
          ['ERC-3643标准', 'T-REX协议', 'MiCA认证'],
          ['ERC-3643 Standard', 'T-REX Protocol', 'MiCA Compliant'],
          ['Standard ERC-3643', 'Protocole T-REX', 'Conforme MiCA'],
          ['ERC-3643標準', 'T-REXプロトコル', 'MiCA準拠'],
          ['ERC-3643-Standard', 'T-REX-Protokoll', 'MiCA-konform'],
          ['ERC-3643 표준', 'T-REX 프로토콜', 'MiCA 준수'],
          ['معيار ERC-3643', 'بروتوكول T-REX', 'متوافق مع MiCA'],
          ['Стандарт ERC-3643', 'Протокол T-REX', 'Соответствие MiCA'],
        ),
        highlight: '欧洲合规标准',
      },
      {
        name: 'HashKey', nameEn: 'HashKey',
        desc: '香港持牌RWA一站式服务平台，提供资产尽调、代币化、发行、交易全流程服务，持有香港SFC VASP牌照。',
        descEn: "Hong Kong's licensed one-stop RWA service platform — from asset due diligence to tokenization, issuance, and trading. SFC VASP licensed.",
        url: 'https://hashkey.com', icon: 'ri-government-line',
        tags: mkTags(
          ['香港牌照', '一站式服务', 'VASP持牌'],
          ['HK Licensed', 'One-Stop Service', 'VASP Licensed'],
          ['Agréé HK', 'Service Complet', 'Agréé VASP'],
          ['香港認可', 'ワンストップサービス', 'VASP認可'],
          ['HK Lizenziert', 'Komplettservice', 'VASP Lizenziert'],
          ['HK 인가', '원스톱 서비스', 'VASP 인가'],
          ['مرخص HK', 'خدمة شاملة', 'مرخص VASP'],
          ['Лицензирован HK', 'Комплексный сервис', 'Лицензирован VASP'],
        ),
        highlight: '香港首选',
      },
      {
        name: 'OSL', nameEn: 'OSL',
        desc: '香港持牌数字资产平台，提供RWA发行、交易、托管一体化服务，持有香港SFC VASP牌照，为亚洲最早合规RWA平台之一。',
        descEn: "Hong Kong SFC-licensed digital asset platform offering integrated RWA issuance, trading, and custody. One of Asia's earliest compliant RWA platforms.",
        url: 'https://osl.com', icon: 'ri-bank-card-line',
        tags: mkTags(
          ['香港牌照', '托管服务', 'VASP持牌'],
          ['HK Licensed', 'Custody Service', 'VASP Licensed'],
          ['Agréé HK', 'Service de Garde', 'Agréé VASP'],
          ['香港認可', 'カストディサービス', 'VASP認可'],
          ['HK Lizenziert', 'Verwahrungsservice', 'VASP Lizenziert'],
          ['HK 인가', '보관 서비스', 'VASP 인가'],
          ['مرخص HK', 'خدمة الحضانة', 'مرخص VASP'],
          ['Лицензирован HK', 'Услуги хранения', 'Лицензирован VASP'],
        ),
      },
      {
        name: 'Polymesh', nameEn: 'Polymesh',
        desc: '全球第一条面向合规资产代币化的许可型区块链，内置KYC/AML模块、合规投票机制、资产确权功能，专为金融机构RWA量身打造。',
        descEn: "The world's first permissioned blockchain purpose-built for compliant asset tokenization — with built-in KYC/AML, compliance voting, and asset confirmation.",
        url: 'https://polymesh.network', icon: 'ri-links-line',
        tags: mkTags(
          ['合规区块链', 'KYC/AML内置', '金融机构专用'],
          ['Compliant Blockchain', 'Built-in KYC/AML', 'Financial Institutions'],
          ['Blockchain Conforme', 'KYC/AML Intégré', 'Institutions Financières'],
          ['コンプライアンスブロックチェーン', 'KYC/AML内蔵', '金融機関専用'],
          ['Compliance-Blockchain', 'Eingebautes KYC/AML', 'Finanzinstitutionen'],
          ['컴플라이언스 블록체인', 'KYC/AML 내장', '금융 기관 전용'],
          ['بلوكتشين متوافق', 'KYC/AML مدمج', 'للمؤسسات المالية'],
          ['Соответствующий блокчейн', 'Встроенный KYC/AML', 'Для финансовых учреждений'],
        ),
      },
      {
        name: 'Ondo Finance', nameEn: 'Ondo Finance',
        desc: '专注金融RWA的代币化平台，推出代币化美债基金Ondo USDY，规模超15亿美元，支持每日赎回，成为散户与机构的RWA入门选择。',
        descEn: 'Financial RWA tokenization platform. Ondo USDY (tokenized US Treasury fund) exceeds $1.5B with daily redemptions — a gateway for both retail and institutional RWA investors.',
        url: 'https://ondo.finance', icon: 'ri-currency-line',
        tags: mkTags(
          ['代币化美债', '散户友好', '每日赎回'],
          ['Tokenized US Treasuries', 'Retail Friendly', 'Daily Redemption'],
          ['Bons du Trésor Tokenisés', 'Accessible aux Particuliers', 'Rachat Quotidien'],
          ['代币化米国債', '個人投資家向け', '毎日償還'],
          ['Tokenisierte US-Staatsanleihen', 'Privatanlegerfreundlich', 'Tägliche Rücknahme'],
          ['토큰화 미국채', '소매 친화적', '일일 환매'],
          ['سندات خزانة أمريكية مرمزة', 'صديق للأفراد', 'استرداد يومي'],
          ['Токенизированные казначейские облигации США', 'Для розничных инвесторов', 'Ежедневное погашение'],
        ),
        highlight: 'TVL $1.5B+',
      },
    ],
  },
  {
    id: 'dev',
    label: '开发者与技术社区',
    labelEn: 'Developer & Tech Communities',
    icon: 'ri-code-box-line',
    color: '#F59E0B',
    items: [
      {
        name: 'GitHub', nameEn: 'GitHub',
        desc: '以太坊、Polkadot、Chainlink、Polymesh等RWA基础设施的开源代码库，代码开源可查，支持二次开发，是区块链开发者的核心工具。',
        descEn: 'Open-source repositories for Ethereum, Chainlink, Polymesh, and other RWA infrastructure. The definitive tool for blockchain developers.',
        url: 'https://github.com', icon: 'ri-github-line',
        tags: mkTags(
          ['开源代码', '技术研发', '协作开发'],
          ['Open Source', 'Tech R&D', 'Collaborative Dev'],
          ['Open Source', 'R&D Technique', 'Développement Collaboratif'],
          ['オープンソース', '技術R&D', '協調開発'],
          ['Open Source', 'Technische F&E', 'Kollaborative Entwicklung'],
          ['오픈 소스', '기술 R&D', '협업 개발'],
          ['مفتوح المصدر', 'البحث والتطوير التقني', 'التطوير التعاوني'],
          ['Открытый исходный код', 'Технические НИОКР', 'Совместная разработка'],
        ),
      },
      {
        name: 'Ethereum Research', nameEn: 'Ethereum Research',
        desc: '以太坊研究者论坛，讨论智能合约优化、Layer2、RWA技术难题，学术氛围浓厚，顶尖专家云集。',
        descEn: "Ethereum researchers' forum for smart contract optimization, Layer 2, and RWA technical challenges. Heavy academic atmosphere, top experts.",
        url: 'https://ethresear.ch', icon: 'ri-flask-line',
        tags: mkTags(
          ['学术研究', 'Layer2', '技术前沿'],
          ['Academic Research', 'Layer 2', 'Tech Frontier'],
          ['Recherche Académique', 'Layer 2', 'Frontière Technologique'],
          ['学術研究', 'Layer2', '技術フロンティア'],
          ['Akademische Forschung', 'Layer 2', 'Technologiegrenze'],
          ['학술 연구', 'Layer 2', '기술 최전선'],
          ['بحث أكاديمي', 'Layer 2', 'الحدود التقنية'],
          ['Академические исследования', 'Layer 2', 'Технологический рубеж'],
        ),
      },
      {
        name: 'OpenZeppelin', nameEn: 'OpenZeppelin',
        desc: '智能合约安全库，提供ERC-20/ERC-721/ERC-1155/ERC-3643等合规代币的安全实现模板，是RWA智能合约开发的安全基础。',
        descEn: 'Smart contract security library with safe implementations of ERC-20/721/1155/3643 and other compliant token standards. The security foundation for RWA smart contracts.',
        url: 'https://openzeppelin.com', icon: 'ri-shield-keyhole-line',
        tags: mkTags(
          ['合约安全', 'ERC标准', '合规模板'],
          ['Contract Security', 'ERC Standards', 'Compliance Templates'],
          ['Sécurité des Contrats', 'Standards ERC', 'Modèles de Conformité'],
          ['コントラクトセキュリティ', 'ERC標準', 'コンプライアンステンプレート'],
          ['Vertragssicherheit', 'ERC-Standards', 'Compliance-Vorlagen'],
          ['계약 보안', 'ERC 표준', '컴플라이언스 템플릿'],
          ['أمان العقود', 'معايير ERC', 'قوالب الامتثال'],
          ['Безопасность контрактов', 'Стандарты ERC', 'Шаблоны соответствия'],
        ),
        highlight: '安全首选',
      },
    ],
  },
  {
    id: 'media',
    label: '资讯与媒体',
    labelEn: 'News & Media',
    icon: 'ri-newspaper-line',
    color: '#0EA5E9',
    items: [
      {
        name: 'CoinDesk', nameEn: 'CoinDesk',
        desc: '全球最权威的加密资产媒体，设有RWA专栏，覆盖项目动态、监管政策、机构动向，报道客观，深度分析多。',
        descEn: "The world's most authoritative crypto media, with a dedicated RWA section covering project updates, regulation, and institutional moves. Objective reporting, deep analysis.",
        url: 'https://www.coindesk.com', icon: 'ri-global-line',
        tags: mkTags(
          ['英文资讯', 'RWA专栏', '国际监管'],
          ['English News', 'RWA Column', 'International Regulation'],
          ['Actualités Anglaises', 'Colonne RWA', 'Réglementation Internationale'],
          ['英語ニュース', 'RWAコラム', '国際規制'],
          ['Englische Nachrichten', 'RWA-Kolumne', 'Internationale Regulierung'],
          ['영어 뉴스', 'RWA 칼럼', '국제 규제'],
          ['أخبار إنجليزية', 'عمود RWA', 'تنظيم دولي'],
          ['Новости на английском', 'Колонка RWA', 'Международное регулирование'],
        ),
        highlight: '权威媒体',
      },
      {
        name: 'The Block', nameEn: 'The Block',
        desc: '聚焦区块链与数字资产的深度调查报道，尤其关注RWA监管动态与机构入场，新闻时效性强，数据支撑充分。',
        descEn: 'Deep investigative blockchain reporting, especially strong on RWA regulatory dynamics and institutional market entry. Fast, data-backed journalism.',
        url: 'https://www.theblock.co', icon: 'ri-news-line',
        tags: mkTags(
          ['深度调查', '监管动态', '机构资讯'],
          ['Investigative', 'Regulatory Updates', 'Institutional News'],
          ['Enquête Approfondie', 'Mises à Jour Réglementaires', 'Actualités Institutionnelles'],
          ['深掘り調査', '規制動向', '機関ニュース'],
          ['Investigativer Journalismus', 'Regulierungsupdates', 'Institutionelle Nachrichten'],
          ['심층 조사', '규제 동향', '기관 뉴스'],
          ['تحقيق معمق', 'تحديثات تنظيمية', 'أخبار مؤسسية'],
          ['Расследовательская журналистика', 'Обновления регуляций', 'Институциональные новости'],
        ),
      },
      {
        name: 'Foresight News（前瞻新闻）', nameEn: 'Foresight News',
        desc: '亚洲领先的Web3媒体，大量覆盖香港、新加坡RWA进展，聚焦亚洲市场，信息精准，中英文内容丰富。',
        descEn: "Asia's leading Web3 media outlet with extensive coverage of Hong Kong and Singapore RWA developments. Precise, bilingual content.",
        url: 'https://foresightnews.pro', icon: 'ri-compass-line',
        tags: mkTags(
          ['亚洲市场', '香港/新加坡', '中英双语'],
          ['Asian Market', 'HK/Singapore', 'Bilingual'],
          ['Marché Asiatique', 'HK/Singapour', 'Bilingue'],
          ['アジア市場', '香港/シンガポール', 'バイリンガル'],
          ['Asiatischer Markt', 'HK/Singapur', 'Zweisprachig'],
          ['아시아 시장', '홍콩/싱가포르', '이중 언어'],
          ['السوق الآسيوي', 'HK/سنغافورة', 'ثنائي اللغة'],
          ['Азиатский рынок', 'HK/Сингапур', 'Двуязычный'],
        ),
        highlight: '亚洲首选',
      },
      {
        name: 'PANews', nameEn: 'PANews',
        desc: '中文社区影响力较大的区块链媒体，报道国内合规RWA案例、政策动态，适合中文用户，国内信息全面。',
        descEn: 'Major Chinese blockchain media outlet covering domestic compliant RWA cases and policy updates. Comprehensive coverage for Chinese-language readers.',
        url: 'https://www.panewslab.com', icon: 'ri-zhihu-line',
        tags: mkTags(
          ['中文资讯', '国内政策', '合规案例'],
          ['Chinese News', 'Domestic Policy', 'Compliance Cases'],
          ['Actualités Chinoises', 'Politique Nationale', 'Cas de Conformité'],
          ['中国語ニュース', '国内政策', 'コンプライアンス事例'],
          ['Chinesische Nachrichten', 'Inlandspolitik', 'Compliance-Fälle'],
          ['중국어 뉴스', '국내 정책', '컴플라이언스 사례'],
          ['أخبار صينية', 'سياسة محلية', 'حالات امتثال'],
          ['Китайские новости', 'Внутренняя политика', 'Случаи соответствия'],
        ),
      },
      {
        name: '链捕手', nameEn: 'ChainCatcher',
        desc: '专注中文区块链与Web3生态的媒体，重点报道RWA、DeFi、监管政策，内容质量较高，适合中国从业者日常阅读。',
        descEn: 'Chinese blockchain and Web3 media focusing on RWA, DeFi, and regulatory policy. High-quality content for Chinese practitioners.',
        url: 'https://www.chaincatcher.com', icon: 'ri-rss-line',
        tags: mkTags(
          ['中文媒体', 'RWA深度', '行业资讯'],
          ['Chinese Media', 'RWA In-depth', 'Industry News'],
          ['Médias Chinois', 'RWA Approfondi', 'Actualités Sectorielles'],
          ['中国語メディア', 'RWA深掘り', '業界ニュース'],
          ['Chinesische Medien', 'RWA Tiefgang', 'Branchennachrichten'],
          ['중국어 미디어', 'RWA 심층', '업계 뉴스'],
          ['وسائل إعلام صينية', 'RWA متعمق', 'أخبار الصناعة'],
          ['Китайские СМИ', 'Углублённый RWA', 'Отраслевые новости'],
        ),
      },
    ],
  },
  {
    id: 'social',
    label: '社交媒体与社区',
    labelEn: 'Social Media & Communities',
    icon: 'ri-community-line',
    color: '#EC4899',
    items: [
      {
        name: 'X (Twitter) — #RWA', nameEn: 'X (Twitter) — #RWA',
        desc: '关注#RWA、#RealWorldAssets等话题，实时获取机构动态、项目进展，信息传播快，全球视野，是RWA行业实时资讯的第一来源。',
        descEn: 'Follow #RWA and #RealWorldAssets for real-time institutional updates and project progress. The fastest global information channel for RWA industry news.',
        url: 'https://twitter.com/search?q=%23RWA', icon: 'ri-twitter-x-line',
        tags: mkTags(
          ['实时资讯', '全球视野', '#RWA话题'],
          ['Real-time News', 'Global View', '#RWA Topic'],
          ['Actualités en Temps Réel', 'Vue Mondiale', 'Sujet #RWA'],
          ['リアルタイムニュース', 'グローバルビュー', '#RWAトピック'],
          ['Echtzeit-Nachrichten', 'Globale Sicht', '#RWA-Thema'],
          ['실시간 뉴스', '글로벌 뷰', '#RWA 주제'],
          ['أخبار فورية', 'رؤية عالمية', 'موضوع #RWA'],
          ['Новости в реальном времени', 'Глобальный взгляд', 'Тема #RWA'],
        ),
        highlight: '信息最快',
      },
      {
        name: 'Discord — RWA项目社群', nameEn: 'Discord — RWA Project Communities',
        desc: 'Centrifuge、Ondo Finance、RealT等主流RWA项目的官方Discord社群，可直接与团队交流，互动性强，一手信息多。',
        descEn: 'Official Discord communities for Centrifuge, Ondo Finance, RealT, and other leading RWA projects. Direct team interaction, first-hand information.',
        url: 'https://discord.com', icon: 'ri-discord-line',
        tags: mkTags(
          ['项目社群', '官方互动', '一手信息'],
          ['Project Community', 'Official Interaction', 'First-hand Info'],
          ['Communauté de Projet', 'Interaction Officielle', 'Info de Première Main'],
          ['プロジェクトコミュニティ', '公式インタラクション', '一次情報'],
          ['Projektgemeinschaft', 'Offizielle Interaktion', 'Ersthand-Info'],
          ['프로젝트 커뮤니티', '공식 상호작용', '일차 정보'],
          ['مجتمع المشروع', 'تفاعل رسمي', 'معلومات مباشرة'],
          ['Сообщество проекта', 'Официальное взаимодействие', 'Информация из первых рук'],
        ),
      },
      {
        name: 'Reddit r/RealWorldAssets', nameEn: 'Reddit r/RealWorldAssets',
        desc: '专注于RWA的Reddit社区，话题涵盖项目分析、技术交流、监管探讨，氛围相对严肃专业，无过多炒作。',
        descEn: 'RWA-focused Reddit community covering project analysis, technical discussion, and regulatory topics. Relatively serious and professional atmosphere.',
        url: 'https://www.reddit.com/r/RealWorldAssets', icon: 'ri-reddit-line',
        tags: mkTags(
          ['社区讨论', '技术交流', '无炒作'],
          ['Community Discussion', 'Tech Exchange', 'No Hype'],
          ['Discussion Communautaire', 'Échange Technique', 'Sans Battage'],
          ['コミュニティ討論', '技術交流', '誇大宣伝なし'],
          ['Community-Diskussion', 'Technischer Austausch', 'Kein Hype'],
          ['커뮤니티 토론', '기술 교류', '과장 없음'],
          ['نقاش مجتمعي', 'تبادل تقني', 'بدون مبالغة'],
          ['Обсуждение сообщества', 'Технический обмен', 'Без хайпа'],
        ),
      },
      {
        name: 'Telegram — RWA研究群', nameEn: 'Telegram — RWA Research Groups',
        desc: '多个专注RWA研究与投资的Telegram群组，是从业者快速交流和获取一手信息的重要渠道，适合深度从业者。',
        descEn: 'Multiple Telegram groups focused on RWA research and investment — an important channel for quick exchange and first-hand information among practitioners.',
        url: 'https://telegram.org', icon: 'ri-telegram-line',
        tags: mkTags(
          ['即时通讯', '研究交流', '从业者群'],
          ['Instant Messaging', 'Research Exchange', 'Practitioner Groups'],
          ['Messagerie Instantanée', 'Échange de Recherche', 'Groupes de Praticiens'],
          ['インスタントメッセージ', '研究交流', '実務者グループ'],
          ['Sofortnachrichten', 'Forschungsaustausch', 'Praktikergruppen'],
          ['인스턴트 메시지', '연구 교류', '실무자 그룹'],
          ['رسائل فورية', 'تبادل بحثي', 'مجموعات الممارسين'],
          ['Мгновенные сообщения', 'Исследовательский обмен', 'Группы практиков'],
        ),
      },
    ],
  },
  {
    id: 'academic',
    label: '学术与研究机构',
    labelEn: 'Academic & Research Institutions',
    icon: 'ri-graduation-cap-line',
    color: '#6366F1',
    items: [
      {
        name: 'IC3 康奈尔区块链研究院', nameEn: 'IC3 — Initiative for Cryptocurrencies & Contracts',
        desc: '康奈尔大学、Cornell Tech区块链与加密货币倡议，联合多所顶尖高校，开展区块链技术与RWA安全的学术研究。',
        descEn: "Cornell University and Cornell Tech's blockchain research initiative, partnering with top universities to advance blockchain security and RWA research.",
        url: 'https://www.initc3.org', icon: 'ri-medal-line',
        tags: mkTags(
          ['学术研究', '区块链安全', '顶尖高校'],
          ['Academic Research', 'Blockchain Security', 'Top Universities'],
          ['Recherche Académique', 'Sécurité Blockchain', 'Meilleures Universités'],
          ['学術研究', 'ブロックチェーンセキュリティ', 'トップ大学'],
          ['Akademische Forschung', 'Blockchain-Sicherheit', 'Top-Universitäten'],
          ['학술 연구', '블록체인 보안', '최고 대학'],
          ['بحث أكاديمي', 'أمان البلوكتشين', 'أفضل الجامعات'],
          ['Академические исследования', 'Безопасность блокчейна', 'Ведущие университеты'],
        ),
      },
      {
        name: 'BIS创新中心', nameEn: 'BIS Innovation Hub',
        desc: '国际清算银行创新中心，发布CBDC、代币化资产（RWA）的实验报告与政策研究，是全球中央银行RWA政策的重要参考来源。',
        descEn: 'Bank for International Settlements Innovation Hub publishing CBDC and tokenized asset (RWA) research reports — the key reference for global central bank RWA policy.',
        url: 'https://www.bis.org/topic/fintech/hub.htm', icon: 'ri-bank-line',
        tags: mkTags(
          ['央行研究', 'CBDC', '国际政策'],
          ['Central Bank Research', 'CBDC', 'International Policy'],
          ['Recherche Banque Centrale', 'CBDC', 'Politique Internationale'],
          ['中央銀行研究', 'CBDC', '国際政策'],
          ['Zentralbankforschung', 'CBDC', 'Internationale Politik'],
          ['중앙은행 연구', 'CBDC', '국제 정책'],
          ['بحث البنك المركزي', 'CBDC', 'سياسة دولية'],
          ['Исследования центрального банка', 'CBDC', 'Международная политика'],
        ),
        highlight: '央行级权威',
      },
      {
        name: 'SSRN区块链与RWA论文库', nameEn: 'SSRN Blockchain & RWA Papers',
        desc: '社会科学研究网络，收录大量关于RWA、代币化资产、区块链金融的学术预印本论文，是学术研究者的首选资源。',
        descEn: 'Social Science Research Network hosts extensive pre-print papers on RWA, tokenized assets, and blockchain finance — the top resource for academic researchers.',
        url: 'https://www.ssrn.com/index.cfm/en/topics/blockchain-cryptoassets', icon: 'ri-file-text-line',
        tags: mkTags(
          ['学术论文', '预印本', '免费获取'],
          ['Academic Papers', 'Pre-prints', 'Free Access'],
          ['Articles Académiques', 'Prépublications', 'Accès Gratuit'],
          ['学術論文', 'プレプリント', '無料アクセス'],
          ['Akademische Arbeiten', 'Vorabdrucke', 'Kostenloser Zugang'],
          ['학술 논문', '프리프린트', '무료 접근'],
          ['أوراق أكاديمية', 'طبعات مسبقة', 'وصول مجاني'],
          ['Академические статьи', 'Препринты', 'Бесплатный доступ'],
        ),
      },
    ],
  },
];

// Localized stats labels
const STATS_LABELS: Record<LangKey, { curated: string; compliant: string; news: string; academic: string; disclaimer: string }> = {
  zh: { curated: '精选平台', compliant: '合规发行平台', news: '资讯媒体渠道', academic: '学术机构', disclaimer: '以上平台链接均为第三方资源，RWA99.cn不对第三方平台内容负责 · 点击访问即代表您将跳转至外部网站' },
  en: { curated: 'Curated Platforms', compliant: 'Compliant Issuance', news: 'News Channels', academic: 'Academic Institutions', disclaimer: 'External platforms listed above. RWA99.cn is not responsible for third-party content · Clicking will navigate to external websites' },
  fr: { curated: 'Plateformes Sélectionnées', compliant: 'Émission Conforme', news: 'Canaux d\'Actualités', academic: 'Institutions Académiques', disclaimer: 'Plateformes externes listées ci-dessus. RWA99.cn n\'est pas responsable du contenu tiers · Cliquer naviguera vers des sites externes' },
  es: { curated: 'Plataformas Seleccionadas', compliant: 'Emisión Conforme', news: 'Canales de Noticias', academic: 'Instituciones Académicas', disclaimer: 'Plataformas externas listadas arriba. RWA99.cn no es responsable del contenido de terceros · Hacer clic navegará a sitios web externos' },
  pt: { curated: 'Plataformas Selecionadas', compliant: 'Emissão Conforme', news: 'Canais de Notícias', academic: 'Instituições Acadêmicas', disclaimer: 'Plataformas externas listadas acima. RWA99.cn não é responsável pelo conteúdo de terceiros · Clicar navegará para sites externos' },
  ja: { curated: '厳選プラットフォーム', compliant: 'コンプライアント発行', news: 'ニュースチャンネル', academic: '学術機関', disclaimer: '上記のプラットフォームリンクはすべてサードパーティのリソースです。RWA99.cnはサードパーティのコンテンツに責任を負いません · クリックすると外部サイトに移動します' },
  de: { curated: 'Ausgewählte Plattformen', compliant: 'Konforme Ausgabe', news: 'Nachrichtenkanäle', academic: 'Akademische Institutionen', disclaimer: 'Oben aufgeführte externe Plattformen. RWA99.cn ist nicht für Inhalte Dritter verantwortlich · Klicken navigiert zu externen Websites' },
  ko: { curated: '엄선된 플랫폼', compliant: '컴플라이언트 발행', news: '뉴스 채널', academic: '학술 기관', disclaimer: '위에 나열된 외부 플랫폼입니다. RWA99.cn은 제3자 콘텐츠에 대해 책임지지 않습니다 · 클릭하면 외부 웹사이트로 이동합니다' },
  ar: { curated: 'منصات مختارة', compliant: 'إصدار متوافق', news: 'قنوات الأخبار', academic: 'مؤسسات أكاديمية', disclaimer: 'المنصات الخارجية المدرجة أعلاه. RWA99.cn غير مسؤول عن محتوى الطرف الثالث · النقر سينقلك إلى مواقع خارجية' },
  ru: { curated: 'Отобранные платформы', compliant: 'Соответствующий выпуск', news: 'Новостные каналы', academic: 'Академические учреждения', disclaimer: 'Внешние платформы, перечисленные выше. RWA99.cn не несёт ответственности за контент третьих сторон · Нажатие перейдёт на внешние сайты' },
};

const HEADER_LABELS: Record<LangKey, { badge: string; titlePre: string; titleHighlight: string; desc: string }> = {
  zh: { badge: '平台与资源导航', titlePre: '国内外优秀', titleHighlight: '平台与社媒', desc: '精选全球区块链与RWA领域最具价值的数据平台、合规发行平台、开发者社区、媒体资讯和学术机构，一键直达' },
  en: { badge: 'Platform & Resource Directory', titlePre: 'Leading Global ', titleHighlight: 'Platforms & Social Media', desc: "Curated selection of the world's most valuable blockchain and RWA data platforms, compliant issuance platforms, developer communities, media outlets, and academic institutions — all accessible with one click" },
  fr: { badge: 'Répertoire de Plateformes et Ressources', titlePre: 'Meilleures ', titleHighlight: 'Plateformes & Médias Sociaux', desc: 'Sélection des plateformes de données blockchain et RWA, plateformes d\'émission conformes, communautés de développeurs, médias et institutions académiques les plus précieux au monde — accessibles en un clic' },
  es: { badge: 'Directorio de Plataformas y Recursos', titlePre: 'Mejores ', titleHighlight: 'Plataformas y Redes Sociales', desc: 'Selección curada de las plataformas de datos blockchain y RWA más valiosas del mundo, plataformas de emisión conformes, comunidades de desarrolladores, medios de comunicación e instituciones académicas — todo accesible con un clic' },
  pt: { badge: 'Diretório de Plataformas e Recursos', titlePre: 'Melhores ', titleHighlight: 'Plataformas e Mídias Sociais', desc: 'Seleção curada das plataformas de dados blockchain e RWA mais valiosas do mundo, plataformas de emissão conformes, comunidades de desenvolvedores, meios de comunicação e instituições acadêmicas — tudo acessível com um clique' },
  ja: { badge: 'プラットフォーム・リソースディレクトリ', titlePre: '世界をリードする', titleHighlight: 'プラットフォーム・ソーシャルメディア', desc: '世界で最も価値あるブロックチェーンとRWAデータプラットフォーム、コンプライアント発行プラットフォーム、開発者コミュニティ、メディア、学術機関を厳選 — ワンクリックでアクセス' },
  de: { badge: 'Plattform- und Ressourcenverzeichnis', titlePre: 'Führende ', titleHighlight: 'Plattformen & Soziale Medien', desc: 'Kuratierte Auswahl der wertvollsten Blockchain- und RWA-Datenplattformen, konformen Ausgabeplattformen, Entwicklergemeinschaften, Medienkanäle und akademischen Institutionen der Welt — alle mit einem Klick erreichbar' },
  ko: { badge: '플랫폼 및 리소스 디렉토리', titlePre: '세계 선도 ', titleHighlight: '플랫폼 및 소셜 미디어', desc: '세계에서 가장 가치 있는 블록체인 및 RWA 데이터 플랫폼, 컴플라이언트 발행 플랫폼, 개발자 커뮤니티, 미디어 채널 및 학술 기관의 엄선된 선택 — 한 번의 클릭으로 모두 접근 가능' },
  ar: { badge: 'دليل المنصات والموارد', titlePre: 'أفضل ', titleHighlight: 'المنصات ووسائل التواصل الاجتماعي', desc: 'اختيار منتقى لأكثر منصات بيانات البلوكتشين وRWA قيمة في العالم، ومنصات الإصدار المتوافقة، ومجتمعات المطورين، وقنوات الإعلام والمؤسسات الأكاديمية — كلها في متناول نقرة واحدة' },
  ru: { badge: 'Каталог платформ и ресурсов', titlePre: 'Ведущие мировые ', titleHighlight: 'Платформы и социальные сети', desc: 'Отобранные наиболее ценные блокчейн и RWA платформы данных, соответствующие платформы выпуска, сообщества разработчиков, медиаканалы и академические учреждения мира — все доступны в один клик' },
};

interface PlatformRating {
  avg: number;
  count: number;
}

function getPlatformRatings(platformName: string): PlatformRating {
  const storageKey = `platform_reviews_${platformName}`;
  const stored = localStorage.getItem(storageKey);
  const userReviews = stored ? JSON.parse(stored) : [];
  // Mock base reviews
  const MOCK_COUNTS: Record<string, { avg: number; count: number }> = {
    'RWA.xyz': { avg: 4.7, count: 3 },
    'Securitize': { avg: 4.5, count: 2 },
    'HashKey': { avg: 4.6, count: 2 },
    'Centrifuge': { avg: 4.4, count: 4 },
    'Ondo Finance': { avg: 4.8, count: 5 },
    'DeFiLlama': { avg: 4.6, count: 3 },
    'OpenZeppelin': { avg: 4.9, count: 6 },
    'CoinDesk': { avg: 4.3, count: 4 },
  };
  const base = MOCK_COUNTS[platformName] ?? { avg: 0, count: 0 };
  if (userReviews.length === 0) return base;
  const userAvg = userReviews.reduce((s: number, r: { rating: number }) => s + r.rating, 0) / userReviews.length;
  const totalCount = base.count + userReviews.length;
  const totalAvg = (base.avg * base.count + userAvg * userReviews.length) / totalCount;
  return { avg: totalAvg, count: totalCount };
}

export default function PlatformDirectory() {
  const [activeTab, setActiveTab] = useState('data');
  const [ratingModal, setRatingModal] = useState<{ name: string; url: string } | null>(null);
  const [ratingsRefresh, setRatingsRefresh] = useState(0);
  const { lang } = useLang();
  const l = lang as LangKey;

  // Refresh ratings when modal closes
  useEffect(() => {
    if (!ratingModal) setRatingsRefresh((v) => v + 1);
  }, [ratingModal]);

  const activeCategory = PLATFORM_DATA.find((c) => c.id === activeTab)!;
  const stats = STATS_LABELS[l] ?? STATS_LABELS['en'];
  const header = HEADER_LABELS[l] ?? HEADER_LABELS['en'];

  return (
    <section id="platforms" className="py-20 bg-[#F8F7FF]">
      <div className="max-w-[1440px] mx-auto px-4 lg:px-8">
        {/* Header */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-4" style={{ background: 'rgba(124,58,237,0.08)', border: '1px solid rgba(124,58,237,0.15)' }}>
            <i className="ri-compass-discover-line text-[#7C3AED] text-sm"></i>
            <span className="text-[#7C3AED] text-sm font-semibold">{header.badge}</span>
          </div>
          <h2 className="text-3xl lg:text-4xl font-black text-gray-900 mb-4">
            {header.titlePre}
            <span className="text-[#7C3AED]">{header.titleHighlight}</span>
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            {header.desc}
          </p>
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap gap-2 justify-center mb-10">
          {PLATFORM_DATA.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveTab(cat.id)}
              className={`px-4 py-2 rounded-full text-sm font-bold transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${
                activeTab === cat.id ? 'text-white shadow-md' : 'bg-white border border-gray-200 text-gray-600 hover:border-gray-300'
              }`}
              style={activeTab === cat.id ? { background: cat.color } : {}}
            >
              <i className={cat.icon}></i>
              {l === 'zh' ? cat.label : cat.labelEn}
              <span className="text-xs opacity-75">({cat.items.length})</span>
            </button>
          ))}
        </div>

        {/* Platform Cards */}
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
          {activeCategory.items.map((item, i) => {
            const rating = getPlatformRatings(item.name);
            return (
              <div
                key={i}
                className="group bg-white border border-gray-100 rounded-2xl p-5 hover:border-gray-200 transition-all duration-300"
              >
                <div className="flex items-start gap-4 mb-3">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl shrink-0 text-white" style={{ background: `linear-gradient(135deg, ${activeCategory.color}, ${activeCategory.color}88)` }}>
                    <i className={`${item.icon} text-xl`}></i>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                      <h3 className="font-black text-gray-900 text-sm">{item.name}</h3>
                      {item.highlight && (
                        <span className="text-xs px-1.5 py-0.5 rounded-full font-bold text-white" style={{ background: activeCategory.color }}>
                          {item.highlight}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-400 truncate">{item.url.replace('https://', '').replace('http://', '')}</p>
                  </div>
                  <a
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <i className="ri-external-link-line text-gray-400 text-sm"></i>
                  </a>
                </div>

                <p className="text-sm text-gray-600 leading-relaxed mb-3 line-clamp-3">
                  {l === 'zh' ? item.desc : item.descEn}
                </p>

                <div className="flex flex-wrap gap-1.5 mb-3">
                  {(item.tags[l] ?? item.tags['en']).map((tag) => (
                    <span key={tag} className="text-xs px-2 py-0.5 rounded-full" style={{ background: `${activeCategory.color}10`, color: activeCategory.color, border: `1px solid ${activeCategory.color}20` }}>
                      {tag}
                    </span>
                  ))}
                </div>

                {/* Rating Row */}
                <div className="flex items-center justify-between pt-3 border-t border-gray-50">
                  <button
                    onClick={() => setRatingModal({ name: item.name, url: item.url })}
                    className="flex items-center gap-1.5 cursor-pointer group/rate"
                  >
                    <div className="flex gap-0.5">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <i
                          key={star}
                          className="ri-star-fill text-xs"
                          style={{ color: rating.count > 0 && rating.avg >= star - 0.5 ? '#F59E0B' : '#E5E7EB' }}
                        ></i>
                      ))}
                    </div>
                    {rating.count > 0 ? (
                      <span className="text-xs text-gray-500">{rating.avg.toFixed(1)} ({rating.count})</span>
                    ) : (
                      <span className="text-xs text-gray-400">暂无评分</span>
                    )}
                  </button>
                  <button
                    onClick={() => setRatingModal({ name: item.name, url: item.url })}
                    className="text-xs font-semibold cursor-pointer hover:opacity-80 transition-opacity px-2.5 py-1 rounded-lg"
                    style={{ color: activeCategory.color, background: `${activeCategory.color}10` }}
                  >
                    <i className="ri-chat-1-line mr-1"></i>评价
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Stats Row */}
        <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { num: `${PLATFORM_DATA.reduce((acc, c) => acc + c.items.length, 0)}+`, label: stats.curated, icon: 'ri-global-line', color: '#7C3AED' },
            { num: `${PLATFORM_DATA.find(c => c.id === 'compliance')!.items.length}+`, label: stats.compliant, icon: 'ri-shield-check-line', color: '#10B981' },
            { num: `${PLATFORM_DATA.find(c => c.id === 'media')!.items.length}+`, label: stats.news, icon: 'ri-newspaper-line', color: '#0EA5E9' },
            { num: `${PLATFORM_DATA.find(c => c.id === 'academic')!.items.length}+`, label: stats.academic, icon: 'ri-graduation-cap-line', color: '#6366F1' },
          ].map((s) => (
            <div key={s.label} className="bg-white border border-gray-100 rounded-2xl px-5 py-4 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl shrink-0" style={{ background: `${s.color}12` }}>
                <i className={`${s.icon} text-base`} style={{ color: s.color }}></i>
              </div>
              <div>
                <div className="text-2xl font-black text-gray-900 leading-none">{s.num}</div>
                <div className="text-xs text-gray-500 mt-0.5">{s.label}</div>
              </div>
            </div>
          ))}
        </div>

        <p className="text-center text-xs text-gray-400 mt-6">
          {stats.disclaimer}
        </p>
      </div>

      {ratingModal && (
        <PlatformRatingModal
          platformName={ratingModal.name}
          platformUrl={ratingModal.url}
          onClose={() => setRatingModal(null)}
        />
      )}
    </section>
  );
}
